<template>
  <div class="page">
    <div class="container">
      <div class="page-header">
        <h1>LO Tracker</h1>
      </div>

      <DataTable 
        :value="records" 
        :paginator="true" 
        :rows="10"
        :loading="loading"
        stripedRows
        showGridlines
        v-model:filters="filters"
        filterDisplay="menu"
      >
        <template #header>
          <div class="table-header">
            <IconField iconPosition="left">
              <InputIcon class="pi pi-search" />
              <InputText v-model="filters['global'].value" placeholder="Search..." />
            </IconField>
          </div>
        </template>
        <Column field="congress_period" header="Congress" sortable />
        <Column field="date_received" header="Date Received" sortable />
        <Column field="agenda" header="Agenda" sortable />
        <Column field="hearing_date" header="Hearing Date" sortable />
        <Column field="hearing_time" header="Hearing Time" sortable />
        <Column field="hearing_place" header="Hearing Place" sortable />
        <Column field="assigned_lo_name" header="Assigned LO" sortable v-if="isLldDc" />

        <Column header="Actions">
          <template #body="{ data }">
            <div class="actions">
              <Button icon="pi pi-pencil" severity="info" size="small" :loading="editLoading === data.id" @click="openEditModal(data)"/>
              <Button v-if="isLldDc" icon="pi pi-users" severity="warn" size="small" @click="openReassignModal(data)" v-tooltip="'Re-assign LO'" />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>

    <Dialog v-model:visible="showModal" header="Record Details" :modal="true" :maximizable="true" :style="{width: '100vw', height: '100vh'}" :contentStyle="{height: '100%'}" position="top">
      <div class="detail-grid">
        <!-- Read-only fields -->
        <div class="detail-row">
          <div class="detail-item">
            <span class="detail-label">Congress No.</span>
            <span class="detail-value">{{ currentRecord.congress_period }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Date Received</span>
            <span class="detail-value">{{ currentRecord.date_received }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Hearing Date</span>
            <span class="detail-value">{{ currentRecord.hearing_date || '—' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Hearing Time</span>
            <span class="detail-value">{{ currentRecord.hearing_time || '—' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Hearing Place</span>
            <span class="detail-value">{{ currentRecord.hearing_place || '—' }}</span>
          </div>
        </div>
        <div class="detail-row">
          <div class="detail-item">
            <span class="detail-label">Agenda</span>
            <span class="detail-value">{{ currentRecord.agenda || '—' }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">Agenda Link</span>
            <span class="detail-value">
              <a v-if="currentRecord.agenda_link" :href="currentRecord.agenda_link" target="_blank" rel="noopener noreferrer">{{ currentRecord.agenda_link }}</a>
              <span v-else>—</span>
            </span>
          </div>
        </div>
        <div class="detail-row">
          <div class="detail-item">
            <span class="detail-label">Referrals</span>
            <span class="detail-value">{{ formatReferrals(currentRecord.referrals) }}</span>
          </div>
        </div>

        <!-- Editable fields -->
        <div class="editable-section">
          <div class="form-group">
            <FloatLabel variant="in">
              <InputText id="edit_long_title" v-model="currentRecord.long_title"/>
              <label for="edit_long_title">Long Title</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <InputText id="edit_short_title" v-model="currentRecord.short_title"/>
              <label for="edit_short_title">Short Title</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <Textarea id="edit_revenue_impact" v-model="currentRecord.revenue_impact_provisions" rows="2"/>
              <label for="edit_revenue_impact">Revenue Impact Provisions *</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <Textarea id="edit_budgetary_impact" v-model="currentRecord.budgetary_impact_provisions" rows="2"/>
              <label for="edit_budgetary_impact">Budgetary Impact Provisions *</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <Textarea id="edit_other_dof_concerns" v-model="currentRecord.other_dof_concerns" rows="2"/>
              <label for="edit_other_dof_concerns">Other DOF Concerns *</label>
            </FloatLabel>
          </div>
          <div class="form-row-3">
            <div class="form-group">
              <FloatLabel variant="in">
                <Select id="edit_bill_category" v-model="currentRecord.bill_category" :options="billCategoryOptions" optionLabel="label" optionValue="value" />
                <label for="edit_bill_category">Bill Category</label>
              </FloatLabel>
            </div>
            <div class="form-group">
              <FloatLabel variant="in">
                <Select id="edit_dof_position" v-model="currentRecord.dof_position" :options="dofPositionOptions" optionLabel="label" optionValue="value" filter />
                <label for="edit_dof_position">DOF Position</label>
              </FloatLabel>
            </div>
            <div class="form-group">
              <FloatLabel variant="in">
                <Select id="edit_dof_actions" v-model="currentRecord.dof_actions_to_be_taken" :options="dofActionsOptions" optionLabel="label" optionValue="value" filter />
                <label for="edit_dof_actions">DOF Actions to be Taken</label>
              </FloatLabel>
            </div>
          </div>
          <div class="form-row-3">
            <div class="form-group">
              <FloatLabel variant="in">
                <Select id="edit_additional_actions" v-model="currentRecord.additional_actions" :options="additionalActionsOptions" optionLabel="label" optionValue="value" />
                <label for="edit_additional_actions">Additional Actions</label>
              </FloatLabel>
            </div>
            <div class="form-group">
              <FloatLabel variant="in">
                <DatePicker id="edit_deadline" v-model="currentRecord.deadline_of_deliverables" dateFormat="yy-mm-dd" />
                <label for="edit_deadline">Deadline of Deliverables</label>
              </FloatLabel>
            </div>
            <div class="form-group">
              <FloatLabel variant="in">
                <Select id="edit_status_deliverables" v-model="currentRecord.status_of_dof_deliverables" :options="statusOfDeliverablesOptions" optionLabel="label" optionValue="value" />
                <label for="edit_status_deliverables">Status of DOF Deliverables</label>
              </FloatLabel>
            </div>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <Textarea id="edit_additional_remarks" v-model="currentRecord.additional_remarks" rows="2"/>
              <label for="edit_additional_remarks">Additional Remarks</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <Textarea id="edit_pending_tasks" v-model="currentRecord.pending_tasks" rows="2"/>
              <label for="edit_pending_tasks">Pending Tasks/Requirements</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <Select id="edit_type_of_meeting" v-model="currentRecord.type_of_meeting" :options="typeOfMeetingOptions" optionLabel="label" optionValue="value" />
              <label for="edit_type_of_meeting">Type of Meeting</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <InputText id="edit_live_link" v-model="currentRecord.live_link"/>
              <label for="edit_live_link">Live Link (FB/Youtube)</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <InputText id="edit_congressional_report_link" v-model="currentRecord.congressional_report_link"/>
              <label for="edit_congressional_report_link">Congressional Report Link</label>
            </FloatLabel>
          </div>
          <div class="form-group">
            <FloatLabel variant="in">
              <Select id="edit_committee_remarks" v-model="currentRecord.committee_remarks" :options="committeeRemarksOptions" optionLabel="label" optionValue="value" />
              <label for="edit_committee_remarks">Committee Remarks</label>
            </FloatLabel>
          </div>
          <Button label="Save Changes" icon="pi pi-save" size="small" @click="saveAgendaChanges" :loading="saving" />
        </div>

        <!-- Actual Attendees Table -->
        <div class="section">
          <div class="section-header">
            <h3>Actual Attendees</h3>
            <Button icon="pi pi-plus" label="Add" size="small" @click="addAttendeeRow" />
          </div>
          <DataTable :value="actualAttendees" showGridlines size="small">
            <Column field="agency_code" header="Agency/Office">
              <template #body="{ data, index }">
                <template v-if="data.editing">
                  <Select v-model="data.agency_code" :options="agencyOptions" optionLabel="name" optionValue="name" class="w-full" filter />
                </template>
                <template v-else>{{ data.agency_code }}</template>
              </template>
            </Column>
            <Column field="names" header="Name/s">
              <template #body="{ data, index }">
                <template v-if="data.editing">
                  <Textarea v-model="data.names" class="w-full" />
                </template>
                <template v-else>{{ data.names }}</template>
              </template>
            </Column>
            <Column header="Actions" style="width: 10rem">
              <template #body="{ data, index }">
                <div class="actions">
                  <Button v-if="data.editing" icon="pi pi-check" severity="success" size="small" :loading="attendeeSaving === index" @click="saveAttendeeRow(data, index)" />
                  <Button v-if="data.editing" icon="pi pi-times" severity="secondary" size="small" :disabled="attendeeSaving === index" @click="cancelAttendeeRow(index)" />
                  <Button v-if="!data.editing" icon="pi pi-pencil" severity="info" size="small" @click="editAttendeeRow(index)" />
                  <Button v-if="!data.editing" icon="pi pi-trash" severity="danger" size="small" @click="deleteAttendeeRow(index)" />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>

        <!-- Key Points Discussed Table -->
        <div class="section">
          <div class="section-header">
            <h3>Key Points Discussed</h3>
            <Button icon="pi pi-plus" label="Add" size="small" @click="addKeyPointRow" />
          </div>
          <DataTable :value="keyPointsDiscussed" showGridlines size="small">
            <Column field="key_point" header="Key Point">
              <template #body="{ data, index }">
                <template v-if="data.editing">
                  <Textarea v-model="data.description" class="w-full" />
                </template>
                <template v-else>{{ data.description }}</template>
              </template>
            </Column>
            <Column header="Actions" style="width: 10rem">
              <template #body="{ data, index }">
                <div class="actions">
                  <Button v-if="data.editing" icon="pi pi-check" severity="success" size="small" :loading="keyPointSaving === index" @click="saveKeyPointRow(data, index)" />
                  <Button v-if="data.editing" icon="pi pi-times" severity="secondary" size="small" :disabled="keyPointSaving === index" @click="cancelKeyPointRow(index)" />
                  <Button v-if="!data.editing" icon="pi pi-pencil" severity="info" size="small" @click="editKeyPointRow(index)" />
                  <Button v-if="!data.editing" icon="pi pi-trash" severity="danger" size="small" @click="deleteKeyPointRow(index)" />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>

        <!-- Status and Action Item Table -->
        <div class="section">
          <div class="section-header">
            <h3>Status and Action Item</h3>
            <Button icon="pi pi-plus" label="Add" size="small" @click="addActionRow" />
          </div>
          <DataTable :value="actionItems" showGridlines size="small">
            <Column field="status" header="Status">
              <template #body="{ data, index }">
                <template v-if="data.editing">
                  <InputText v-model="data.status" class="w-full" />
                </template>
                <template v-else>{{ data.status }}</template>
              </template>
            </Column>
            <Column field="action_item" header="Action Item">
              <template #body="{ data, index }">
                <template v-if="data.editing">
                  <InputText v-model="data.action_item" class="w-full" />
                </template>
                <template v-else>{{ data.action_item }}</template>
              </template>
            </Column>
            <Column header="Actions" style="width: 10rem">
              <template #body="{ data, index }">
                <div class="actions">
                  <Button v-if="data.editing" icon="pi pi-check" severity="success" size="small" :loading="actionSaving === index" @click="saveActionRow(data, index)" />
                  <Button v-if="data.editing" icon="pi pi-times" severity="secondary" size="small" :disabled="actionSaving === index" @click="cancelActionRow(index)" />
                  <Button v-if="!data.editing" icon="pi pi-pencil" severity="info" size="small" @click="editActionRow(index)" />
                  <Button v-if="!data.editing" icon="pi pi-trash" severity="danger" size="small" @click="deleteActionRow(index)" />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>

        <!-- Legislative Status Table -->
        <div class="section">
          <div class="section-header">
            <h3>Legislative Status</h3>
            <Button icon="pi pi-plus" label="Add" size="small" @click="addLegislativeRow" />
          </div>
          <DataTable :value="legislativeStatuses" showGridlines size="small">
            <Column field="status" header="Status">
              <template #body="{ data, index }">
                <template v-if="data.editing">
                  <Select v-model="data.status" :options="legislativeStatusOptions" optionLabel="label" optionValue="value" class="w-full" />
                </template>
                <template v-else>{{ data.status }}</template>
              </template>
            </Column>
            <Column field="date" header="Date">
              <template #body="{ data, index }">
                <template v-if="data.editing">
                  <DatePicker v-model="data.date" dateFormat="yy-mm-dd" class="w-full" />
                </template>
                <template v-else>{{ data.date }}</template>
              </template>
            </Column>
            <Column header="Actions" style="width: 10rem">
              <template #body="{ data, index }">
                <div class="actions">
                  <Button v-if="data.editing" icon="pi pi-check" severity="success" size="small" :loading="legislativeSaving === index" @click="saveLegislativeRow(data, index)" />
                  <Button v-if="data.editing" icon="pi pi-times" severity="secondary" size="small" :disabled="legislativeSaving === index" @click="cancelLegislativeRow(index)" />
                  <Button v-if="!data.editing" icon="pi pi-pencil" severity="info" size="small" @click="editLegislativeRow(index)" />
                  <Button v-if="!data.editing" icon="pi pi-trash" severity="danger" size="small" @click="deleteLegislativeRow(index)" />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>

      <div class="modal-actions">
        <Button type="button" label="Close" severity="secondary" icon="pi pi-times" @click="closeModal" />
      </div>
    </Dialog>

    <Dialog v-model:visible="showReassignModal" header="Re-assign Liaison Officer" :modal="true" :style="{width: '450px'}">
      <div class="reassign-form">
        <p>Agenda: <strong>{{ reassignRecord?.agenda }}</strong></p>
        <div class="form-group">
          <FloatLabel variant="in">
            <Select id="reassign_lo" v-model="reassignLoId" :options="loUsers" optionLabel="name" optionValue="id" filter class="w-full" />
            <label for="reassign_lo">Assign to LO</label>
          </FloatLabel>
        </div>
      </div>
      <div class="modal-actions">
        <Button type="button" label="Cancel" severity="secondary" icon="pi pi-times" @click="showReassignModal = false" />
        <Button type="button" label="Re-assign" icon="pi pi-check" :loading="reassigning" @click="saveReassign" />
      </div>
    </Dialog>
  </div>
</template>


<script setup>
import { ref, computed, onMounted, getCurrentInstance } from 'vue'
import Swal from 'sweetalert2'
import api from '../axios.js'

const { proxy } = getCurrentInstance()

const records = ref([])
const showModal = ref(false)
const saving = ref(false)
const loading = ref(false)
const editLoading = ref(null)
const currentRecord = ref({})
const actionItems = ref([])
const legislativeStatuses = ref([])
const actualAttendees = ref([])
const keyPointsDiscussed = ref([])
const agencyOptions = ref([])
const actionSaving = ref(null)
const legislativeSaving = ref(null)
const attendeeSaving = ref(null)
const keyPointSaving = ref(null)
const loUsers = ref([])
const showReassignModal = ref(false)
const reassignRecord = ref(null)
const reassignLoId = ref(null)
const reassigning = ref(false)

const isLldDc = computed(() => proxy.$authStore.user?.role?.includes('lld-dc'))

const filters = ref({
  global: { value: null, matchMode: 'contains' }
})

const legislativeStatusOptions = [
  { label: 'Filed', value: 'Filed' },
  { label: 'Pending in Committee', value: 'Pending in Committee' },
  { label: 'Approved on 2nd Reading', value: 'Approved on 2nd Reading' },
  { label: 'Approved on 3rd Reading', value: 'Approved on 3rd Reading' },
  { label: 'Transmitted to Senate', value: 'Transmitted to Senate' },
  { label: 'Transmitted to House', value: 'Transmitted to House' },
  { label: 'Ratified', value: 'Ratified' },
  { label: 'Signed into Law', value: 'Signed into Law' },
  { label: 'Vetoed', value: 'Vetoed' }
]

const billCategoryOptions = [
  { label: 'RED Bill', value: 'RED Bill' },
  { label: 'LEDAC', value: 'LEDAC' },
  { label: 'DOF Priority', value: 'DOF Priority' },
  { label: 'Others', value: 'Others' }
]

const positionPaperStatusOptions = [
  { label: 'Submitted', value: 'Submitted' },
  { label: 'Pending', value: 'Pending' },
  { label: 'Not Required', value: 'Not Required' }
]

const dofPositionOptions = [
  { label: 'Deferred', value: 'Deferred'},
  { label: 'Deferred with comments', value: 'Deferred with comments'},
  { label: 'Deferred with reservations', value: 'Deferred with reservations'},
  { label: 'Do not support', value: 'Do not support'},
  { label: 'Provisions are no longer necessary', value: 'Provisions are no longer necessary'},
  { label: 'Support', value: 'Support'},
  { label: 'With comments', value: 'With comments'},
  { label: 'With recommendations', value: 'With recommendations'},
  { label: 'With reservations', value: 'With reservations'},
]

const dofActionsOptions = [
  { label: 'DOF position was not requested', value: 'DOF position was not requested' },
  { label: 'DOF position paper has been signed and submitted', value: 'DOF position paper has been signed and submitted' },
  { label: 'DOF to attend TWG discussion', value: 'DOF to attend TWG discussion' },
  { label: 'For submission of cost-benefit analysis', value: 'For submission of cost-benefit analysis' },
  { label: 'For submission of data/information request', value: 'For submission of data/information request' },
  { label: 'For submission of data/study', value: 'For submission of data/study' },
  { label: 'For submission of DOF position paper', value: 'For submission of DOF position paper' },
  { label: 'For submission of DOF position paper and requested data', value: 'For submission of DOF position paper and requested data' },
  { label: 'For submission of joint position paper', value: 'For submission of joint position paper' },
  { label: 'For submission of proposed language', value: 'For submission of proposed language' },
  { label: 'For submission of suspension order', value: 'For submission of suspension order' },
  { label: 'For submission of updated DOF position paper', value: 'For submission of updated DOF position paper' },
  { label: 'None', value: 'None' },
  { label: 'Other', value: 'Other' },
]

const additionalActionsOptions = [
  { label: 'For submission of data', value: 'For submission of data'},
  { label: 'For submission of study', value: 'For submission of study'},
  { label: 'For submission of proposed language', value: 'For submission of proposed language'},
  { label: 'DOF to attend TWG discussion', value: 'DOF to attend TWG discussion'},
  { label: 'For submission of cost-benefit analysis', value: 'For submission of cost-benefit analysis'},
  { label: 'For submission of suspension order', value: 'For submission of suspension order'},
]

const statusOfDeliverablesOptions = [
  { label: 'Ongoing', value: 'Ongoing'},
  { label: 'No deliverables', value: 'No deliverables'},
  { label: 'Deliverables submitted', value: 'Deliverables submitted'},
]

const typeOfMeetingOptions = [
 { label: 'Committee hearing', value: 'Committee hearing' },
 { label: 'Consultative meeting', value: 'Consultative meeting' },
 { label: 'Briefing', value: 'Briefing' },
 { label: 'TWG', value: 'TWG' },
 { label: 'Pre-bicam', value: 'Pre-bicam' },
 { label: 'Bicam', value: 'Bicam' },
]

const committeeRemarksOptions = [
  { label: "Approved by secondary committee; for approval of primary committee", value: "Approved by secondary committee; for approval of primary committee" },
  { label: "Approved by the committee", value: "Approved by the committee" },
  { label: "Approved on 2nd reading", value: "Approved on 2nd reading" },
  { label: "Briefing suspended", value: "Briefing suspended" },
  { label: "Committee hearing adjourned", value: "Committee hearing adjourned" },
  { label: "Committee hearing suspended", value: "Committee hearing suspended" },
  { label: "Committee report approved and submitted", value: "Committee report approved and submitted" },
  { label: "Committee report approved and submitted. Pending approval on 2nd reading.", value: "Committee report approved and submitted. Pending approval on 2nd reading." },
  { label: "For approval in the committee", value: "For approval in the committee" },
  { label: "For bicameral confrence committee meeting", value: "For bicameral confrence committee meeting" },
  { label: "For committee report", value: "For committee report" },
  { label: "For finalization of committee report", value: "For finalization of committee report" },
  { label: "For further committee hearing", value: "For further committee hearing" },
  { label: "For further TWG discussion", value: "For further TWG discussion" },
  { label: "For TWG discussion", value: "For TWG discussion" },
  { label: "None", value: "None" },
  { label: "Pending approval on 2nd reading (for interpellation)", value: "Pending approval on 2nd reading (for interpellation)" },
  { label: "Pending approval on 2nd reading (for sponsorship)", value: "Pending approval on 2nd reading (for sponsorship)" },
  { label: "Referred to secondary committee (Appropriations)", value: "Referred to secondary committee (Appropriations)" },
  { label: "Referred to secondary committee (WAM)", value: "Referred to secondary committee (WAM)" },
  { label: "Referred to sub committees", value: "Referred to sub committees" },
  { label: "The committee hearing was followed by an executive session", value: "The committee hearing was followed by an executive session" },
  { label: "The TWG report will be submitted to the mother committee", value: "The TWG report will be submitted to the mother committee" },
  { label: "TWG adjourned", value: "TWG adjourned" },
  { label: "TWG adjourned, for finalization of committee report", value: "TWG adjourned, for finalization of committee report" },
  { label: "TWG discussion suspended.", value: "TWG discussion suspended." },
  { label: "Unnumbered substitute bill and committee report approved by the committee", value: "Unnumbered substitute bill and committee report approved by the committee" },
  { label: "Unnumbered substitute bill approved by the committee", value: "Unnumbered substitute bill approved by the committee" }
]

const loadRecords = async () => {
  try {
    loading.value = true
    const response = await api.get('/lo-tracker')
    records.value = response.data
  } catch (error) {
    console.error('Error loading records:', error)
  } finally {
    loading.value = false
  }
}

const loadAgencies = async () => {
  try {
    const response = await api.get('/agencies')
    agencyOptions.value = response.data
  } catch (error) {
    console.error('Error loading agencies:', error)
  }
}

const loadLoUsers = async () => {
  try {
    const response = await api.get('/lo-officers')
    loUsers.value = response.data
  } catch (error) {
    console.error('Error loading LO users:', error)
  }
}

const openReassignModal = (record) => {
  reassignRecord.value = record
  reassignLoId.value = record.assigned_lo
  showReassignModal.value = true
}

const saveReassign = async () => {
  if (!reassignLoId.value) return
  const result = await Swal.fire({
    title: 'Re-assign LO?',
    text: 'Are you sure you want to re-assign this item to a different Liaison Officer?',
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Re-assign',
    cancelButtonText: 'Cancel'
  })
  if (!result.isConfirmed) return

  try {
    reassigning.value = true
    await api.put(`/lo-tracker/${reassignRecord.value.id}/reassign`, {
      assigned_lo: reassignLoId.value
    })
    showReassignModal.value = false
    loadRecords()
    Swal.fire({ title: 'Saved!', text: 'LO re-assigned successfully', icon: 'success', timer: 1500, showConfirmButton: false })
  } catch (error) {
    Swal.fire({ title: 'Error!', text: 'Failed to re-assign', icon: 'error' })
  } finally {
    reassigning.value = false
  }
}

const getAgencyName = (id) => {
  const agency = agencyOptions.value.find(a => a.id === id)
  return agency ? agency.name : '—'
}

const formatReferrals = (referrals) => {
  if (!referrals || !referrals.length) return '—'
  return referrals.map(code => {
    const agency = agencyOptions.value.find(a => a.code === code)
    return agency ? `${agency.name} (${agency.code})` : code
  }).join(', ')
}

const openEditModal = async (record) => {
  editLoading.value = record.id
  try {
    const response = await api.get(`/lo-tracker/${record.id}`)
    currentRecord.value = response.data
    actionItems.value = (response.data.action_items || []).map(item => ({ ...item, editing: false }))
    legislativeStatuses.value = (response.data.legislative_statuses || []).map(item => ({ ...item, editing: false }))
    actualAttendees.value = (response.data.attendees || []).map(item => ({ ...item, editing: false }))
    keyPointsDiscussed.value = (response.data.key_points_discussed || []).map(item => ({ ...item, editing: false }))
    showModal.value = true
  } catch (error) {
    console.error('Error loading record:', error)
  } finally {
    editLoading.value = null
  }
}

const closeModal = () => {
  showModal.value = false
}

const saveAgendaChanges = async () => {
  try {
    saving.value = true
    await api.put(`/lo-tracker/${currentRecord.value.id}`, {
      agenda: currentRecord.value.agenda,
      agenda_link: currentRecord.value.agenda_link,
      long_title: currentRecord.value.long_title,
      short_title: currentRecord.value.short_title,
      revenue_impact_provisions: currentRecord.value.revenue_impact_provisions,
      budgetary_impact_provisions: currentRecord.value.budgetary_impact_provisions,
      other_dof_concerns: currentRecord.value.other_dof_concerns,
      bill_category: currentRecord.value.bill_category,
      dof_position: currentRecord.value.dof_position,
      dof_actions_to_be_taken: currentRecord.value.dof_actions_to_be_taken,
      additional_actions: currentRecord.value.additional_actions,
      deadline_of_deliverables: currentRecord.value.deadline_of_deliverables,
      status_of_dof_deliverables: currentRecord.value.status_of_dof_deliverables,
      additional_remarks: currentRecord.value.additional_remarks,
      pending_tasks: currentRecord.value.pending_tasks,
      type_of_meeting: currentRecord.value.type_of_meeting,
      live_link: currentRecord.value.live_link,
      congressional_report_link: currentRecord.value.congressional_report_link,
      committee_remarks: currentRecord.value.committee_remarks
    })
    loadRecords()
    Swal.fire({ title: 'Saved!', text: 'Changes saved successfully', icon: 'success' })
  } catch (error) {
    Swal.fire({ title: 'Error!', text: 'Failed to save changes', icon: 'error' })
  } finally {
    saving.value = false
  }
}

// ============================================================================
// ACTION ITEMS CRUD
// ============================================================================

const addActionRow = () => {
  actionItems.value.push({ status: '', action_item: '', editing: true, isNew: true })
}

const editActionRow = (index) => {
  actionItems.value[index]._backup = { status: actionItems.value[index].status, action_item: actionItems.value[index].action_item }
  actionItems.value[index].editing = true
}

const cancelActionRow = (index) => {
  if (actionItems.value[index].isNew) {
    actionItems.value.splice(index, 1)
  } else {
    const backup = actionItems.value[index]._backup
    actionItems.value[index].status = backup.status
    actionItems.value[index].action_item = backup.action_item
    actionItems.value[index].editing = false
  }
}

const saveActionRow = async (data, index) => {
  try {
    actionSaving.value = index
    if (data.isNew) {
      const response = await api.post(`/lo-tracker/${currentRecord.value.id}/action-items`, {
        status: data.status,
        action_item: data.action_item
      })
      actionItems.value[index] = { ...response.data, editing: false }
    } else {
      await api.put(`/lo-tracker/${currentRecord.value.id}/action-items/${data.id}`, {
        status: data.status,
        action_item: data.action_item
      })
      actionItems.value[index].editing = false
    }
    delete actionItems.value[index].isNew
    delete actionItems.value[index]._backup
  } catch (error) {
    Swal.fire({ title: 'Error!', text: 'Failed to save', icon: 'error' })
  } finally {
    actionSaving.value = null
  }
}

const deleteActionRow = async (index) => {
  const item = actionItems.value[index]
  const result = await Swal.fire({
    title: 'Delete?',
    text: 'Are you sure?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    confirmButtonColor: '#ef4444'
  })
  if (result.isConfirmed) {
    try {
      await api.delete(`/lo-tracker/${currentRecord.value.id}/action-items/${item.id}`)
      actionItems.value.splice(index, 1)
    } catch (error) {
      Swal.fire({ title: 'Error!', text: 'Failed to delete', icon: 'error' })
    }
  }
}

// ============================================================================
// ACTUAL ATTENDEES CRUD
// ============================================================================

const addAttendeeRow = () => {
  actualAttendees.value.push({ agency_code: null, names: '', editing: true, isNew: true })
}

const editAttendeeRow = (index) => {
  actualAttendees.value[index]._backup = { agency_code: actualAttendees.value[index].agency_code, name: actualAttendees.value[index].names }
  actualAttendees.value[index].editing = true
}

const cancelAttendeeRow = (index) => {
  if (actualAttendees.value[index].isNew) {
    actualAttendees.value.splice(index, 1)
  } else {
    const backup = actualAttendees.value[index]._backup
    actualAttendees.value[index].agency_code = backup.agency_code
    actualAttendees.value[index].names = backup.names
    actualAttendees.value[index].editing = false
  }
}

const saveAttendeeRow = async (data, index) => {
  try {
    attendeeSaving.value = index
    if (data.isNew) {
      const response = await api.post(`/lo-tracker/${currentRecord.value.id}/actual-attendees`, {
        agency_code: data.agency_code,
        names: data.names
      })
      actualAttendees.value[index] = { ...response.data, editing: false }
    } else {
      await api.put(`/lo-tracker/${currentRecord.value.id}/actual-attendees/${data.id}`, {
        agency_code: data.agency_code,
        names: data.names
      })
      actualAttendees.value[index].editing = false
    }
    delete actualAttendees.value[index].isNew
    delete actualAttendees.value[index]._backup
  } catch (error) {
    Swal.fire({ title: 'Error!', text: 'Failed to save', icon: 'error' })
  } finally {
    attendeeSaving.value = null
  }
}

const deleteAttendeeRow = async (index) => {
  const item = actualAttendees.value[index]
  const result = await Swal.fire({
    title: 'Delete?',
    text: 'Are you sure?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    confirmButtonColor: '#ef4444'
  })
  if (result.isConfirmed) {
    try {
      await api.delete(`/lo-tracker/${currentRecord.value.id}/actual-attendees/${item.id}`)
      actualAttendees.value.splice(index, 1)
    } catch (error) {
      Swal.fire({ title: 'Error!', text: 'Failed to delete', icon: 'error' })
    }
  }
}

// ============================================================================
// KEY POINTS DISCUSSED CRUD
// ============================================================================

const addKeyPointRow = () => {
  keyPointsDiscussed.value.push({ description: '', editing: true, isNew: true })
}

const editKeyPointRow = (index) => {
  keyPointsDiscussed.value[index]._backup = { description: keyPointsDiscussed.value[index].description }
  keyPointsDiscussed.value[index].editing = true
}

const cancelKeyPointRow = (index) => {
  if (keyPointsDiscussed.value[index].isNew) {
    keyPointsDiscussed.value.splice(index, 1)
  } else {
    keyPointsDiscussed.value[index].description = keyPointsDiscussed.value[index]._backup.description
    keyPointsDiscussed.value[index].editing = false
  }
}

const saveKeyPointRow = async (data, index) => {
  try {
    keyPointSaving.value = index
    if (data.isNew) {
      const response = await api.post(`/lo-tracker/${currentRecord.value.id}/key-points`, {
        description: data.description
      })
      keyPointsDiscussed.value[index] = { ...response.data, editing: false }
    } else {
      await api.put(`/lo-tracker/${currentRecord.value.id}/key-points/${data.id}`, {
        description: data.description
      })
      keyPointsDiscussed.value[index].editing = false
    }
    delete keyPointsDiscussed.value[index].isNew
    delete keyPointsDiscussed.value[index]._backup
  } catch (error) {
    Swal.fire({ title: 'Error!', text: 'Failed to save', icon: 'error' })
  } finally {
    keyPointSaving.value = null
  }
}

const deleteKeyPointRow = async (index) => {
  const item = keyPointsDiscussed.value[index]
  const result = await Swal.fire({
    title: 'Delete?',
    text: 'Are you sure?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    confirmButtonColor: '#ef4444'
  })
  if (result.isConfirmed) {
    try {
      await api.delete(`/lo-tracker/${currentRecord.value.id}/key-points/${item.id}`)
      keyPointsDiscussed.value.splice(index, 1)
    } catch (error) {
      Swal.fire({ title: 'Error!', text: 'Failed to delete', icon: 'error' })
    }
  }
}

// ============================================================================
// LEGISLATIVE STATUS CRUD
// ============================================================================

const addLegislativeRow = () => {
  legislativeStatuses.value.push({ status: '', date: null, editing: true, isNew: true })
}

const editLegislativeRow = (index) => {
  legislativeStatuses.value[index]._backup = { status: legislativeStatuses.value[index].status, date: legislativeStatuses.value[index].date }
  legislativeStatuses.value[index].editing = true
}

const cancelLegislativeRow = (index) => {
  if (legislativeStatuses.value[index].isNew) {
    legislativeStatuses.value.splice(index, 1)
  } else {
    const backup = legislativeStatuses.value[index]._backup
    legislativeStatuses.value[index].status = backup.status
    legislativeStatuses.value[index].date = backup.date
    legislativeStatuses.value[index].editing = false
  }
}

const saveLegislativeRow = async (data, index) => {
  try {
    legislativeSaving.value = index
    if (data.isNew) {
      const response = await api.post(`/lo-tracker/${currentRecord.value.id}/legislative-statuses`, {
        status: data.status,
        date: data.date
      })
      legislativeStatuses.value[index] = { ...response.data, editing: false }
    } else {
      await api.put(`/lo-tracker/${currentRecord.value.id}/legislative-statuses/${data.id}`, {
        status: data.status,
        date: data.date
      })
      legislativeStatuses.value[index].editing = false
    }
    delete legislativeStatuses.value[index].isNew
    delete legislativeStatuses.value[index]._backup
  } catch (error) {
    Swal.fire({ title: 'Error!', text: 'Failed to save', icon: 'error' })
  } finally {
    legislativeSaving.value = null
  }
}

const deleteLegislativeRow = async (index) => {
  const item = legislativeStatuses.value[index]
  const result = await Swal.fire({
    title: 'Delete?',
    text: 'Are you sure?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: 'Delete',
    confirmButtonColor: '#ef4444'
  })
  if (result.isConfirmed) {
    try {
      await api.delete(`/lo-tracker/${currentRecord.value.id}/legislative-statuses/${item.id}`)
      legislativeStatuses.value.splice(index, 1)
    } catch (error) {
      Swal.fire({ title: 'Error!', text: 'Failed to delete', icon: 'error' })
    }
  }
}

onMounted(() => {
  loadRecords()
  loadAgencies()
  if (isLldDc.value) loadLoUsers()
})
</script>

<style scoped>
.page {
  padding: 1rem 0;
  background: var(--p-surface-50);
  min-height: 100vh;
}

.container {
  backdrop-filter: blur(10px);
  border-radius: 20px;
  margin: 1rem;
  padding: 1.5rem;
  width: calc(100vw - 2rem);
  max-width: none;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #1e3a8a;
}

.page-header h1 {
  color: #1e3a8a;
  font-size: 2rem;
  font-weight: 700;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.actions {
  display: flex;
  gap: 0.5rem;
}

.detail-grid {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
  max-height: 70vh;
  overflow-y: auto;
}

.detail-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.detail-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
}

.detail-value {
  font-size: 0.95rem;
  color: #1e293b;
  font-weight: 500;
}

.editable-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.editable-section .p-button {
  align-self: flex-start;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group .p-inputtext,
.form-group .p-textarea,
.form-group .p-select,
.form-group .p-datepicker {
  width: 100% !important;
}

:deep(.p-datatable .p-inputtext),
:deep(.p-datatable .p-textarea),
:deep(.p-datatable .p-select),
:deep(.p-datatable .p-datepicker) {
  width: 100% !important;
}

.form-row-3 {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

@media (max-width: 992px) {
  .form-row-3 {
    grid-template-columns: 1fr;
  }
}

.section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.section-header h3 {
  color: #1e3a8a;
  margin: 0;
  font-size: 1.1rem;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.5rem;
  border-top: 1px solid #e5e7eb;
  background: #f8fafc;
}

@media (max-width: 768px) {
  .detail-row {
    grid-template-columns: 1fr;
  }
}

.reassign-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem 0;
}

.reassign-form .p-select {
  width: 100% !important;
}
</style>
